import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, timeout: 15000 });

export const fetchServerStatus = () => client.get("/server-status").then(r => r.data);
export const fetchStats = () => client.get("/stats").then(r => r.data);
export const fetchServers = () => client.get("/servers").then(r => r.data);
export const fetchOnlinePlayers = () => client.get("/players/online").then(r => r.data);
export const fetchLivePlayers = () => client.get("/players/live").then(r => r.data);
export const fetchEvents = (params = {}) => {
  const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== "" && v !== null)).toString();
  return client.get(`/events${q ? `?${q}` : ""}`).then(r => r.data);
};
export const fetchEvent = (slug) => client.get(`/events/${slug}`).then(r => r.data);
export const fetchEventCategories = () => client.get("/events/categories").then(r => r.data);
export const searchEvents = (q) => client.get(`/events/search?q=${encodeURIComponent(q)}`).then(r => r.data);
export const reportEvent = (slug, payload) => client.post(`/events/${slug}/report`, payload).then(r => r.data);
export const registerTeam = (slug, payload) => client.post(`/events/${slug}/teams`, payload).then(r => r.data);
export const fetchTeams = (slug) => client.get(`/events/${slug}/teams`).then(r => r.data);

// -------- Communities --------
export const fetchCommunities = (params = {}) => {
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== "" && v !== null)).toString();
  return client.get(`/communities${qs ? `?${qs}` : ""}`).then(r => r.data);
};
export const fetchCommunity = (slug) => client.get(`/communities/${slug}`).then(r => r.data);
export const fetchCommunityCategories = () => client.get("/communities/categories").then(r => r.data);
export const createCommunity = (payload) => client.post("/communities", payload).then(r => r.data);
export const updateCommunity = (slug, payload) => client.put(`/communities/${slug}`, payload).then(r => r.data);
export const deleteCommunity = (slug) => client.delete(`/communities/${slug}`).then(r => r.data);
export const joinCommunity = (slug) => client.post(`/communities/${slug}/join`).then(r => r.data);
export const leaveCommunity = (slug) => client.delete(`/communities/${slug}/join`).then(r => r.data);
export const fetchCommunityMembers = (slug, params = {}) => {
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== "" && v !== null)).toString();
  return client.get(`/communities/${slug}/members${qs ? `?${qs}` : ""}`).then(r => r.data);
};
export const setCommunityRole = (slug, user_id, role) => client.put(`/communities/${slug}/members/${user_id}/role`, { role }).then(r => r.data);
export const kickCommunityMember = (slug, user_id) => client.delete(`/communities/${slug}/members/${user_id}`).then(r => r.data);
export const fetchCommunityMedia = (slug) => client.get(`/communities/${slug}/media`).then(r => r.data);
export const fetchCommunityAnalytics = (slug) => client.get(`/communities/${slug}/analytics`).then(r => r.data);
export const pinCommunityPost = (slug, post_id) => client.post(`/communities/${slug}/posts/${post_id}/pin`).then(r => r.data);
export const featureCommunityPost = (slug, post_id) => client.post(`/communities/${slug}/posts/${post_id}/feature`).then(r => r.data);
export const hideCommunityPost = (slug, post_id) => client.delete(`/communities/${slug}/posts/${post_id}`).then(r => r.data);
export const fetchBuilds = () => client.get("/builds").then(r => r.data);
export const fetchLeaderboard = (cat) => client.get(`/leaderboards/${cat}`).then(r => r.data);
export const fetchStore = () => client.get("/store").then(r => r.data);
export const fetchAnnouncements = () => client.get("/announcements").then(r => r.data);
export const fetchActivity = () => client.get("/activity").then(r => r.data);
export const fetchSettings = () => client.get("/settings").then(r => r.data);
export const submitContact = (payload) => client.post("/contact", payload).then(r => r.data);
export const mojangLookup = (username) => client.get(`/mojang/${encodeURIComponent(username)}`).then(r => r.data);

// Creators (public)
export const fetchCreators = (featured = false) => client.get(`/creators${featured ? "?featured=true" : ""}`).then(r => r.data);
export const fetchCreator = (id) => client.get(`/creators/${id}`).then(r => r.data);

// Users (public profile)
export const fetchUser = (idOrHandle) => {
  const raw = String(idOrHandle || "");
  const path = raw.startsWith("@")
    ? `/users/${encodeURIComponent(raw)}`   // GET /users/@handle
    : `/users/${encodeURIComponent(raw)}`;
  return client.get(path).then(r => r.data);
};
export const fetchUserFollowers = (id) => client.get(`/users/${id}/followers`).then(r => r.data);
export const fetchUserFollowing = (id) => client.get(`/users/${id}/following`).then(r => r.data);
export const fetchUserMutuals = (id) => client.get(`/users/${id}/mutuals`).then(r => r.data);
export const fetchSuggestedUsers = (limit = 8) => client.get(`/users/suggested?limit=${limit}`).then(r => r.data);
export const searchUsers = (q) => client.get(`/users/search?q=${encodeURIComponent(q)}`).then(r => r.data);

// Server detail (dedicated page)
export const fetchServerDetail = (key) => client.get(`/servers/${key}`).then(r => r.data);

// Config (badges, socials, ranks, countries, home_sections, staff)
export const fetchConfig = () => client.get("/config").then(r => r.data);

// Feed
export const fetchFeed = (params = {}) => {
  const q = new URLSearchParams(params).toString();
  return client.get(`/feed${q ? `?${q}` : ""}`).then(r => r.data);
};
export const fetchTrendingTags = () => client.get("/feed/tags/trending").then(r => r.data);

// -------- Global Search --------
export const globalSearch = (q, type = "all", limit = 10) => {
  const params = new URLSearchParams({ q: q || "", type: type || "all", limit: String(limit) });
  return client.get(`/search?${params}`).then(r => r.data);
};
export const fetchSearchTrending = (limit = 10) => client.get(`/search/trending?limit=${limit}`).then(r => r.data);
export const logSearchQuery = (q) => client.post("/search/log", { q }).then(r => r.data).catch(() => null);
// YouTube (public)
export const fetchYoutubeVideos = (limit = 24, liveOnly = false) =>
  client.get(`/youtube/videos?limit=${limit}&live_only=${liveOnly}`).then(r => r.data);
export const fetchLiveStreams = () => client.get("/youtube/live").then(r => r.data);

// Admin
const adminHeaders = () => ({ "X-Admin-Token": localStorage.getItem("lostdc:admin") || "" });
export const adminVerify = (token) => client.post("/admin/verify", null, { headers: { "X-Admin-Token": token } }).then(r => r.data);
export const adminServers = {
  list: () => client.get("/admin/servers", { headers: adminHeaders() }).then(r => r.data),
  create: (p) => client.post("/admin/servers", p, { headers: adminHeaders() }).then(r => r.data),
  update: (id, p) => client.put(`/admin/servers/${id}`, p, { headers: adminHeaders() }).then(r => r.data),
  remove: (id) => client.delete(`/admin/servers/${id}`, { headers: adminHeaders() }).then(r => r.data),
};
export const adminAnnouncements = {
  list: () => client.get("/admin/announcements", { headers: adminHeaders() }).then(r => r.data),
  create: (p) => client.post("/admin/announcements", p, { headers: adminHeaders() }).then(r => r.data),
  update: (id, p) => client.put(`/admin/announcements/${id}`, p, { headers: adminHeaders() }).then(r => r.data),
  remove: (id) => client.delete(`/admin/announcements/${id}`, { headers: adminHeaders() }).then(r => r.data),
};
export const adminEvents = {
  list: () => client.get("/admin/events", { headers: adminHeaders() }).then(r => r.data),
  create: (p) => client.post("/admin/events", p, { headers: adminHeaders() }).then(r => r.data),
  update: (id, p) => client.put(`/admin/events/${id}`, p, { headers: adminHeaders() }).then(r => r.data),
  remove: (id) => client.delete(`/admin/events/${id}`, { headers: adminHeaders() }).then(r => r.data),
};
export const adminSaveSettings = (p) => client.put("/admin/settings", p, { headers: adminHeaders() }).then(r => r.data);

// Phase 8 — Admin Dashboard (unified)
export const adminDashboard = {
  users: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== "" && v !== null)).toString();
    return client.get(`/admin/dashboard/users${qs ? `?${qs}` : ""}`, { headers: adminHeaders() }).then(r => r.data);
  },
  userDetail: (id) => client.get(`/admin/dashboard/users/${id}`, { headers: adminHeaders() }).then(r => r.data),
  updateUser: (id, patch) => client.put(`/admin/dashboard/users/${id}`, patch, { headers: adminHeaders() }).then(r => r.data),
  deleteUser: (id) => client.delete(`/admin/dashboard/users/${id}`, { headers: adminHeaders() }).then(r => r.data),
  moderationQueue: (limit = 100) => client.get(`/admin/dashboard/moderation/queue?limit=${limit}`, { headers: adminHeaders() }).then(r => r.data),
  resolveReport: (kind, id, status = "resolved") => client.post(`/admin/dashboard/moderation/${kind}/${id}/resolve`, { status }, { headers: adminHeaders() }).then(r => r.data),
  youtube: () => client.get("/admin/dashboard/youtube", { headers: adminHeaders() }).then(r => r.data),
  syncCreator: (id) => client.post(`/admin/dashboard/youtube/${id}/sync`, null, { headers: adminHeaders() }).then(r => r.data),
  analytics: () => client.get("/admin/dashboard/analytics", { headers: adminHeaders() }).then(r => r.data),
  analyticsTimeseries: (days = 14) => client.get(`/admin/dashboard/analytics/timeseries?days=${days}`, { headers: adminHeaders() }).then(r => r.data),
};

export const SERVER_IP = "play.lostdrkc.in";
export const DISCORD_URL = "https://discord.gg/lostdc";
